"use client";

import React from "react";

import { cn } from "@/lib/utils";
import { animate, useMotionValue, useTransform, useVelocity, type MotionValue } from "framer-motion";

import { MotionSpan } from "@/components/global/utils/motionTags";

/**
 * An odometer readout: each digit slot is a one-line window onto a ten-digit
 * wheel, and a change scrolls the wheel to the new digit rather than swapping
 * the glyph. Only the digits that actually differ move, so 2019 → 2022 rolls the
 * units three places and the tens one, and the leading "20" never budges — which
 * is what makes it read as the same number changing rather than a new one.
 *
 * The wheel is continuous in both directions: digit cells are placed by their
 * *shortest signed distance* from the current position, so 9 → 0 rolls one step
 * on past the end instead of winding all the way back through 8..1. Cells that
 * wrap to the far side of the wheel do so well outside the window, unseen.
 */
const WHEEL = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
const WHEEL_HALF = WHEEL.length / 2;

/**
 * Loose enough to overshoot a little on arrival — the settle is most of what
 * sells the weight of a number this size.
 */
const DIGIT_SPRING = {
  type: "spring",
  stiffness: 165,
  damping: 23,
  mass: 1,
} as const;

/**
 * The carry: each slot starts a beat after the one to its right, so a change
 * ripples leftward the way a mechanical counter does instead of every digit
 * lurching at once.
 */
const CARRY_STEP = 0.055;

/**
 * Motion blur, in em so it tracks the type size across breakpoints. Velocity is
 * in wheel steps per second — a single-digit roll peaks around 6, a longer one
 * well past that, so the cap lands on the fast part of a multi-step spin.
 */
const BLUR_PEAK_VELOCITY = 11;
const BLUR_MAX = 0.035;

/** Signed distance from `position` to `digit`, taking the short way round. */
const wheelOffset = (distance: number) => {
  const wrapped = ((distance % WHEEL.length) + WHEEL.length) % WHEEL.length;
  return wrapped > WHEEL_HALF ? wrapped - WHEEL.length : wrapped;
};

function WheelDigit({ digit, position }: { digit: number; position: MotionValue<number> }) {
  // percentage of the cell's own height, which is one slot — so the wheel keeps
  // its spacing at any font size without measuring anything
  const y = useTransform(position, (value) => `${wheelOffset(digit - value) * 100}%`);

  return (
    <MotionSpan
      style={{ y }}
      className="block absolute inset-0 text-center">
      {digit}
    </MotionSpan>
  );
}

/**
 * @param order the whole number this digit belongs to — a slot can't tell which
 *   way to turn from its own digit alone (2019 → 2022 counts *up*, even though
 *   the units go 9 → 2), so it takes the direction from the number as a whole.
 */
function DigitSlot({ digit, order, delay }: { digit: number; order: number; delay: number }) {
  // Absolute position on the wheel rather than the digit itself: it accumulates
  // past 9 and below 0 so the spring always travels the way the year is going.
  const position = useMotionValue(digit);
  const target = React.useRef(digit);
  const previousOrder = React.useRef(order);
  const controls = React.useRef<ReturnType<typeof animate> | null>(null);

  React.useEffect(() => {
    const from = previousOrder.current;
    previousOrder.current = order;

    const current = ((target.current % WHEEL.length) + WHEEL.length) % WHEEL.length;
    const forward = (digit - current + WHEEL.length) % WHEEL.length;
    // this slot's digit is unchanged — the roll belongs to another one
    if (forward === 0) return;

    // forward is the distance going up; the same move going down is that minus a
    // full turn, which is how a countdown gets 2 → 9 as one step back
    target.current += order < from ? forward - WHEEL.length : forward;

    // deliberately not stopped on re-run: a new `animate` supersedes the old one,
    // whereas cleaning up would strand a slot mid-roll whenever the number changed
    // again before it landed and this slot had nothing new to do
    controls.current = animate(position, target.current, { ...DIGIT_SPRING, delay });
  }, [digit, order, delay, position]);

  React.useEffect(() => () => controls.current?.stop(), []);

  const velocity = useVelocity(position);
  const blur = useTransform(velocity, (value) => {
    const amount = Math.min(Math.abs(value) / BLUR_PEAK_VELOCITY, 1) * BLUR_MAX;
    return amount < 0.0005 ? "none" : `blur(${amount.toFixed(4)}em)`;
  });

  return (
    /*
      The window. `clip-path` rather than `overflow-hidden`, because an
      inline-block with overflow takes its baseline from its bottom margin edge,
      which would drop every slot below the characters beside it.

      It has to cut top and bottom but stay clear of the sides, or the blur halo
      would end on a hard vertical edge. The clip runs along the border box, so
      the padding is what buys that room — and the matching negative margin hands
      it straight back, leaving slots sitting flush against each other.
    */
    <span
      style={{ clipPath: "inset(0)", padding: "0 0.08em", margin: "0 -0.08em" }}
      className="inline-block relative">
      {/*
        An out-of-flow wheel can't size its own window, so a hidden zero holds the
        slot open. `tabular-nums` on the readout is what keeps that width honest
        for the digits actually passing through it.
      */}
      <span className="invisible">0</span>

      <MotionSpan
        style={{ filter: blur }}
        className="block absolute inset-0">
        {WHEEL.map((value) => (
          <WheelDigit
            key={value}
            digit={value}
            position={position}
          />
        ))}
      </MotionSpan>
    </span>
  );
}

/**
 * @param value    the number to display, as written (e.g. a year)
 * @param reduceMotion render as plain text, with no wheel and no roll
 */
export default function MorphingNumber({
  value,
  reduceMotion,
  className,
}: {
  value: string | number | undefined;
  reduceMotion?: boolean;
  className?: string;
}) {
  const text = value == null ? "" : String(value);
  // what the slots turn towards: strip anything that isn't a digit so a value
  // written "2019/20" still compares as one number
  const order = Number(text.replace(/\D/g, ""));

  if (reduceMotion) return <span className={className}>{text}</span>;

  const characters = text.split("");

  return (
    <span className={cn("inline-flex tabular-nums", className)}>
      <span className="sr-only">{text}</span>

      <span
        aria-hidden
        className="inline-flex">
        {characters.map((character, index) => {
          // slots are keyed from the right so a shorter or longer value lines up
          // under the one before it instead of shifting every digit along
          const key = characters.length - 1 - index;

          if (!/\d/.test(character))
            return (
              <span key={key}>{character}</span>
            );

          return (
            <DigitSlot
              key={key}
              digit={Number(character)}
              order={order}
              delay={key * CARRY_STEP}
            />
          );
        })}
      </span>
    </span>
  );
}
